import { useCallback, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { describeError } from "../utils/error_message";
import { USER_NAME_MAX_LENGTH } from "../constants/user_input";
import UserLocationSelect from "../components/user_location_select";
import { useUserDataContext } from "../../user_data_context";
import { normalizeEmail } from "./auth_validation";
import { errorStatus, sendRegistrationCode, type RegistrationDraft } from "./auth_api";
import { authErrorKey, validateRegistration, type FieldErrors } from "./auth_rules";
import {
	AuthField,
	FormError,
	GoogleButton,
	OrDivider,
	PRIMARY_BUTTON_CLASS,
	passwordsMatch,
} from "./auth_fields";

interface Props {
	/** Held by the modal so "Back" from the code screen brings it back intact. */
	draft: RegistrationDraft;
	/** An updater, not a value: the place picker reports country and its id in two calls in a row. */
	updateDraft: (update: (draft: RegistrationDraft) => RegistrationDraft) => void;
	onCodeSent: () => void;
	/** Where Google brings the reader back. */
	returnTo: string;
	onLeaveForGoogle?: () => void;
}

const RegistrationForm = ({
	draft,
	updateDraft,
	onCodeSent,
	returnTo,
	onLeaveForGoogle,
}: Props) => {
	const { t } = useTranslation();
	const { interfaceLanguage } = useUserDataContext();
	const [errors, setErrors] = useState<FieldErrors>({});
	const [formError, setFormError] = useState("");
	const [loading, setLoading] = useState(false);
	// The picker prefills from this once; built a single time so its effect does not rerun on every render.
	const [placeSeed] = useState(() => ({
		user_country: draft.country,
		user_city: draft.city,
		user_place_details: {
			country: draft.countryId ? { place_id: draft.countryId } : undefined,
			locality: draft.cityId ? { place_id: draft.cityId } : undefined,
		},
	}));

	const set = useCallback(
		(key: keyof RegistrationDraft, value: string | null) => {
			updateDraft(d => ({ ...d, [key]: key === "email" ? normalizeEmail(value ?? "") : value }));
			setErrors(prev => ({ ...prev, [key]: undefined }));
			setFormError("");
		},
		[updateDraft],
	);

	const submit = async (e: FormEvent) => {
		e.preventDefault();
		if (loading) return;
		const found = validateRegistration(draft);
		setErrors(found);
		setFormError("");
		if (Object.keys(found).length) return;

		setLoading(true);
		try {
			await sendRegistrationCode({
				email: draft.email,
				name: draft.name.trim(),
				lang: interfaceLanguage,
			});
			// The form stays mounted behind the code screen; "Back" must find it ready again.
			setLoading(false);
			onCodeSent();
		} catch (error) {
			console.error("Landing registration code failed", describeError(error));
			const { field, key } = authErrorKey("send-code", errorStatus(error));
			if (field === "email") setErrors(prev => ({ ...prev, email: key }));
			else setFormError(key);
			setLoading(false);
		}
	};

	return (
		<form className="flex flex-col gap-3" onSubmit={submit} noValidate>
			<AuthField
				type="email"
				required
				autoComplete="email"
				data-testid="auth-email-input"
				placeholder={t("auth-page.inputs.email-placeholder")}
				value={draft.email}
				error={errors.email}
				onChange={e => set("email", e.target.value)}
			/>
			<div className="grid grid-cols-2 gap-3 max-[420px]:grid-cols-1">
				<AuthField
					required
					autoComplete="given-name"
					maxLength={USER_NAME_MAX_LENGTH}
					data-testid="auth-name-input"
					placeholder={t("auth-page.inputs.name-placeholder")}
					value={draft.name}
					error={errors.name}
					onChange={e => set("name", e.target.value)}
				/>
				<AuthField
					autoComplete="family-name"
					maxLength={USER_NAME_MAX_LENGTH}
					data-testid="auth-last-name-input"
					placeholder={t("auth-page.inputs.second-name-placeholder")}
					value={draft.secondName}
					onChange={e => set("secondName", e.target.value)}
				/>
			</div>
			<AuthField
				type="password"
				required
				autoComplete="new-password"
				data-testid="auth-password-input"
				placeholder={t("auth-page.inputs.password-placeholder")}
				value={draft.password}
				error={errors.password}
				onChange={e => set("password", e.target.value)}
			/>
			<AuthField
				type="password"
				required
				autoComplete="new-password"
				data-testid="auth-confirm-password-input"
				placeholder={t("auth-page.inputs.confirm-password-placeholder")}
				value={draft.confirmPassword}
				error={errors.confirmPassword}
				confirmed={passwordsMatch(draft.password, draft.confirmPassword)}
				onChange={e => set("confirmPassword", e.target.value)}
			/>
			<UserLocationSelect
				inComponent="auth"
				userData={placeSeed}
				handleUserData={(key: keyof RegistrationDraft, value: string | null) => set(key, value)}
			/>
			<FormError error={formError} />
			<button
				type="submit"
				className={PRIMARY_BUTTON_CLASS}
				disabled={loading}
				data-testid="send-form-button"
			>
				{t(loading ? "auth-page.buttons.registration-loading" : "auth.sign-up")}
			</button>
			<OrDivider />
			<GoogleButton returnTo={returnTo} onLeave={onLeaveForGoogle}>
				{t("auth-page.buttons.continue-with-google")}
			</GoogleButton>
		</form>
	);
};

export default RegistrationForm;
